import _pick from 'lodash/pick'
import _groupBy from 'lodash/groupBy'
import EventBus from '@/eventBus'
import ObjectService from '@/vuex/modules/history/ObjectService'
import SlideOptionApi from '@/api/slideOption'
import SlideApi from '@/api/slide'
import Socket from '@/socket'
import PresentationApi from '@/api/presentation'
import { isFeatureEnabled } from '@/vuex/modules/featureToggles.module'
import utils from '@/util'
import { debouncedFetchMisspells } from '@/business-logic/spellchecker'
import Constant from '@/constant'

const correctOrderChanges = 'correct-order-changes'
const redrawMasonry = 'redraw-masonry-correct-order'
const updateCanvasContent = 'update-correct-order-canvas-content'
function emitGlobalInputUpdate() {
  setTimeout(() => {
    EventBus.$emit(redrawMasonry)
    EventBus.$emit(updateCanvasContent)
    EventBus.$emit(correctOrderChanges)
    EventBus.$emit('update-correct-order-canvas-content')
    EventBus.$emit('update-title-option')
    EventBus.$emit('changeSlideOption')
    EventBus.$emit('match-pairs-change')
    EventBus.$emit('update-field-match-pair')
    EventBus.$emit('on-generate-options')
  }, 300)
}
function getLastSlide(slides) {
  if (!slides.length) {
    return ''
  }

  return slides[slides.length - 1].id
}
const SUPPRESS_TIME_OUT = 150
const MAX_PAST_COUNT = 100
const TRACKED_ATTRIBUTES_SLIDES = ['id', 'deleted', 'order']
const TRACKED_ATTRIBUTES_ACTIVE_SLIDE = ['id', 'title', { SlideOptions: ['order', 'correct', 'title', 'image'] },
  'type', 'canvasBlocks', 'image', 'matchingQuestionOptions', 'correctQuizTypeAnswer']
const TRACKED_ATTRIBUTES_SLIDE_OPTIONS = ['deleted', 'order', 'title', 'correct', 'image']
const _state = {
  pasts: [],
  futures: [],
  present: null,
  suppressHistory: false,
}

const getters = {
  canUseUndoRedoV2: (_, __, ___, rootGetters) => {
    return isFeatureEnabled(rootGetters, 'release-undo-redo-v2')
  },
  canForward: (state) => {
    return state.futures.length > 0 && !state.suppressHistory
  },
  canBackward: (state) => {
    return state.pasts.length > 0 && !state.suppressHistory
  },
}

const mutations = {
  SET_PRESENT(state, { present }) {
    if (state.suppressHistory) {
      return
    }
    const serializedPresent = ObjectService.serialize(present)

    if (state.present !== null) {
      if (state.pasts.length >= MAX_PAST_COUNT) {
        state.pasts.shift()
      }
      state.pasts.push(state.present)
    }
    state.futures = []
    state.present = serializedPresent
  },

  BACKWARD(state) {
    if (state.pasts.length > 0) {
      state.futures.push(state.present)
      state.present = state.pasts.pop()
    }
  },
  CLEAR_HISTORY(state) {
    state.pasts = []
    state.futures = []
    state.present = null
  },

  FORWARD(state) {
    if (state.futures.length > 0) {
      state.pasts.push(state.present)
      state.present = state.futures.pop()
    }
  },

  SET_SUPPRESS_HISTORY(state, { suppress }) {
    state.suppressHistory = suppress
  },
}
/**
 * Processes each detected change in slide options.
 */
function processSlideOptions(detectedOptions, presentationId) {
  return detectedOptions.map((change) => {
    if (!change) return null
    switch (change.action) {
      case 'swap':
        return SlideOptionApi.updateSlideOption(change?.id, {
          order: change.order.new,
          presentationId,
        })
      case 'update':
        return SlideOptionApi.updateSlideOption(change?.id, {
          ..._pick(change.diffs, TRACKED_ATTRIBUTES_SLIDE_OPTIONS),
          presentationId,
        })
      case 'add':
        return SlideOptionApi.updateSlideOption(change?.item?.id, {
          ..._pick(change.item, TRACKED_ATTRIBUTES_SLIDE_OPTIONS),
          deleted: false,
          presentationId,
        })
      case 'delete':
        return SlideOptionApi.deleteSlideOption(change?.id, { presentationId })
      default:
        return null
    }
  })
}
function getUpdatedSlideActive(changedKeys, historyActiveSlide) {
  const updatedSlideActive = {}
  // only update changed keys
  changedKeys.forEach((changeKey) => {
    updatedSlideActive[changeKey] = historyActiveSlide[changeKey]
  })
  return updatedSlideActive
}

// handle swap , add, delete slides
function processSlide(detectedOptions, presentationId, historySlides) {
  const groupedChanges = _groupBy(detectedOptions, 'action')
  if (Object.keys(groupedChanges).length === 0) {
    return null
  }
  if (groupedChanges?.delete?.length > 0) {
    const deletedSlides = groupedChanges.delete
    return SlideApi.updateSlides({
      presentationId,
      socketId: Socket.getSocketId(),
      slides: deletedSlides.map((s) => ({
        id: s?.id,
        deleted: true,
      })),
    })
  }
  if (groupedChanges?.add?.length > 0) {
    return SlideApi.updateSlides({
      presentationId,
      socketId: Socket.getSocketId(),
      slides: historySlides.map((s) => ({
        id: s?.id,
        order: s.order,
        deleted: false,
      })),
    })
  }
  if (groupedChanges?.swap?.length > 0) {
    const swapSlides = groupedChanges.swap
    swapSlides.map((swapSlide) => {
      return SlideApi.updateSlideById(swapSlide?.id, {
        order: swapSlide.order.new,
        presentationId,
      })
    })
    return null
  }
  return null
}

/**
 * Updates the slides list if changes are detected.
 */
async function historyUpdateSlides(historySlides, rootPresentation, commit, dispatch) {
  const rootSlides = rootPresentation.slides
  const presentationId = rootPresentation.id
  const slideChanges = ObjectService.detectArrayChanges(rootSlides, historySlides, TRACKED_ATTRIBUTES_SLIDES)
  processSlide(slideChanges, presentationId, historySlides)
  const detectedSwapAction = slideChanges.find((change) => change.action === 'swap')
  if (detectedSwapAction) {
    dispatch('updateLastSlideAction')
  }
  const payload = {
    slides: historySlides,
  }
  dispatch('patchPresentationAction', payload, { root: true })
  if (historySlides?.length > 0) {
    // make sure to select the first slide
    const selectID = slideChanges[0]?.item?.id
    EventBus.$emit('SideLeft: deselectAllSlides')
    EventBus.$emit('makeSlideActive', historySlides.find((s) => selectID === s.id))
  }
}
async function historyUpdateActiveSlide({
  historyActiveSlide, rootSlideActive, rootSlides, presentationId,
}) {
  const rootTargetSlide = ObjectService.findInArrayByKeyValue(rootSlides, 'id', historyActiveSlide.id)[0]
  const changedKeys = ObjectService.diffKeys(historyActiveSlide, rootTargetSlide, TRACKED_ATTRIBUTES_ACTIVE_SLIDE)

  // Process slide options if they have changed.
  if (changedKeys?.includes('SlideOptions')) {
    const detectedOptionsChanges = ObjectService.detectArrayChanges(
      rootTargetSlide.SlideOptions,
      historyActiveSlide.SlideOptions,
      TRACKED_ATTRIBUTES_SLIDE_OPTIONS,
    )
    processSlideOptions(detectedOptionsChanges, presentationId)
  } else {
    // case for update slide
    changedKeys.map((changeKey) => {
      const payload = {
        presentationId,
      }
      let value = historyActiveSlide[changeKey]
      // case handle for set image slide
      if (changeKey === 'image') {
        value = historyActiveSlide[changeKey]?.name
      }
      payload[changeKey] = value
      SlideApi.updateSlideById(historyActiveSlide.id, payload)
      return null
    })
  }

  const updatedSlideActive = getUpdatedSlideActive(changedKeys, historyActiveSlide)

  EventBus.$emit('makeSlideActive', { ...rootSlideActive, ...updatedSlideActive })

  emitGlobalInputUpdate()
}
const determineSnapshotEligibility = (slide, keyChanges) => {
  // case word cloud submit change slide options so we need to ignore
  if (keyChanges?.includes('SlideOptions') && slide.type === Constant.SLIDE_TYPE.WORD_CLOUD) {
    return { isEligible: false }
  }
  return { isEligible: true }
}

const actions = {
  setSuppressHistoryAction({ commit }, suppress) {
    commit('SET_SUPPRESS_HISTORY', { suppress })
  },

  snapshotSlidesAction({
    commit, state, dispatch, rootState,
  }, { slideId, slides }) {
    if (!state.present) {
      commit('SET_PRESENT', { present: { slideId, slides } })
      return
    }

    const history = JSON.parse(state.present)
    const prunedHistorySlides = ObjectService.serialize(history.slides, TRACKED_ATTRIBUTES_SLIDES)
    const prunedSlides = ObjectService.serialize(slides, TRACKED_ATTRIBUTES_SLIDES)

    const activeSlide = ObjectService.findInArrayByKeyValue(slides, 'id', slideId)[0]
    const historyActiveSlide = ObjectService.findInArrayByKeyValue(history.slides, 'id', history.slideId)[0]
    const activeSlideKeyChanges = ObjectService
      .diffKeys(activeSlide, historyActiveSlide, TRACKED_ATTRIBUTES_ACTIVE_SLIDE)
    const slideIdDiff = slideId !== history.slideId && slideId !== null && history.slideId !== null
    const { isEligible } = determineSnapshotEligibility(activeSlide, activeSlideKeyChanges)
    if ((prunedHistorySlides !== prunedSlides || slideIdDiff || activeSlideKeyChanges.length > 0) && isEligible) {
      commit('SET_PRESENT', { present: { slideId, slides } })
      // call spellchecker to get misspells
      debouncedFetchMisspells(dispatch, rootState.presentation?.id)
    }
  },
  // After swapping slides, this function should be called to update the last slide.
  async updateLastSlideAction({ rootState, commit }) {
    const { presentation } = rootState
    const idLastSlide = getLastSlide(presentation.slides)
    const payload = {
      socketId: Socket.getSocketId(),
    }
    if (idLastSlide && idLastSlide !== presentation.lastSlide) {
      payload.lastSlide = idLastSlide
      commit('updatePresentation', { lastSlide: idLastSlide }, { root: true })
      await PresentationApi.updatePresentationByPresentationId(presentation.id, payload)
    }
  },

  async historyUpdatePresentationAction({
    dispatch, commit, rootState, state,
  }) {
    const presentationId = rootState.presentation.id
    const history = JSON.parse(state.present)
    const rootSlides = rootState.presentation.slides
    const rootSlideActive = rootState.slideActive
    // Compare and update slides list if necessary.
    const prunedHistorySlides = ObjectService.serialize(history.slides, TRACKED_ATTRIBUTES_SLIDES)
    const prunedRootSlides = ObjectService.serialize(rootSlides, TRACKED_ATTRIBUTES_SLIDES)
    if (prunedHistorySlides !== prunedRootSlides) {
      await historyUpdateSlides(history.slides, rootState.presentation, commit, dispatch)
      return
    }

    // Determine the active slide from history.
    const historyActiveSlide = ObjectService.findInArrayByKeyValue(history.slides, 'id', history.slideId)[0]

    // Update active slide if IDs differ.
    if (history.slideId !== rootState.slideActive?.id) {
      const changedKeys = ObjectService.diffKeys(historyActiveSlide,
        rootState.slideActive, TRACKED_ATTRIBUTES_ACTIVE_SLIDE)
      const updatedSlideActive = getUpdatedSlideActive(changedKeys, historyActiveSlide)
      const trueSlideActive = rootSlides
        .find((slide) => slide.id === history.slideId)
      if (trueSlideActive?.id) {
        EventBus.$emit('makeSlideActive', { ...trueSlideActive, ...updatedSlideActive })
      }
    } else {
      await historyUpdateActiveSlide({
        historyActiveSlide,
        rootSlides,
        presentationId,
        rootSlideActive,
      })
    }
  },

  async undoSlidesAction({ commit, dispatch, state }) {
    await dispatch('setSyncPresentation', 1, { root: true })
    commit('SET_SUPPRESS_HISTORY', { suppress: true })
    if (state.pasts.length > 0) {
      commit('BACKWARD')
      await dispatch('historyUpdatePresentationAction')
    }
    await dispatch('setSyncPresentation', 2, { root: true })
    await utils.sleep(SUPPRESS_TIME_OUT)
    commit('SET_SUPPRESS_HISTORY', { suppress: false })
  },

  async redoSlidesAction({ commit, dispatch, state }) {
    dispatch('setSyncPresentation', 1, { root: true })
    commit('SET_SUPPRESS_HISTORY', { suppress: true })
    if (state.futures.length > 0) {
      commit('FORWARD')
      await dispatch('historyUpdatePresentationAction')
    }
    dispatch('setSyncPresentation', 2, { root: true })
    await utils.sleep(SUPPRESS_TIME_OUT)
    commit('SET_SUPPRESS_HISTORY', { suppress: false })
  },
  clearHistoryAction({ commit }) {
    commit('CLEAR_HISTORY')
  },

}

export default {
  namespaced: true,
  state: _state,
  getters,
  mutations,
  actions,
}
